import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class CookieService {
  private readonly document = inject(DOCUMENT) as Document;

  private readonly REFRESH_TOKEN_KEY = 'refresh_token';

  /**
   * Set a secure cookie with proper security flags
   */
  setCookie(name: string, value: string, days: number = 1): void {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    const cookieOptions = [
      `${name}=${encodeURIComponent(value)}`,
      `expires=${expires}`,
      `path=/`,
      `SameSite=Strict`,
      ...(location.protocol === 'https:' ? ['Secure'] : []),
    ].join('; ');

    this.document.cookie = cookieOptions;
  }

  /**
   * Get cookie value by name
   */
  getCookie(name: string): string | null {
    const nameEQ = `${name}=`;
    const cookies = this.document.cookie.split(';');
    for (let cookie of cookies) {
      let c = cookie.trim();
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }
    return null;
  }

  /**
   * Remove cookie by setting expiration to past date
   */
  removeCookie(name: string): void {
    const cookieOptions = [
      `${name}=`,
      `expires=Thu, 01 Jan 1970 00:00:00 UTC`,
      `path=/`,
      `SameSite=Strict`,
    ].join('; ');

    this.document.cookie = cookieOptions;
  }

  /**
   * Check if cookie exists
   */
  hasCookie(name: string): boolean {
    return this.getCookie(name) !== null;
  }

  /**
   * Get refresh token from cookie
   */
  getRefreshToken(): string | null {
    return this.getCookie(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Set refresh token in secure cookie
   */
  setRefreshToken(token: string, days: number = 30): void {
    this.setCookie(this.REFRESH_TOKEN_KEY, token, days);
  }

  /**
   * Remove refresh token cookie
   */
  removeRefreshToken(): void {
    this.removeCookie(this.REFRESH_TOKEN_KEY);
  }
}
